import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-2">文章未找到</h2>
            <p className="text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link to="/articles" className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold rounded-full bg-primary-500 text-background-50 cursor-pointer whitespace-nowrap transition-all hover:bg-primary-600">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i> 返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.excerpt,
    'author': { '@type': 'Person', 'name': article.author },
    'datePublished': article.date,
    'image': article.coverImage,
    'publisher': {
      '@type': 'Organization',
      'name': '银河娱乐官方平台',
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://51xlzxs.cn/articles/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': article.title,
    'description': article.excerpt,
    'url': `https://51xlzxs.cn/articles/${article.slug}`,
    'breadcrumb': {
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://51xlzxs.cn/' },
        { '@type': 'ListItem', 'position': 2, 'name': '最新资讯', 'item': 'https://51xlzxs.cn/articles' },
        { '@type': 'ListItem', 'position': 3, 'name': article.title, 'item': `https://51xlzxs.cn/articles/${article.slug}` },
      ],
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 银河娱乐官方平台`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://51xlzxs.cn/articles/${article.slug}`}
        ogType="article"
        schemaData={{ ...schemaData, ...breadcrumbSchema }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100">
          <div className="max-w-4xl mx-auto pt-8 pb-4">
            <Breadcrumb currentTitle={article.title} />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          {/* Cover Image */}
          <div className="rounded-xl overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full aspect-[2/1] object-cover object-top"
            />
          </div>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">{article.category}</span>
            <span className="text-sm text-foreground-500">{article.date}</span>
            <span className="text-sm text-foreground-500 flex items-center gap-1">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              {article.readTime}
            </span>
            <span className="text-sm text-foreground-500 flex items-center gap-1">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {article.author}
            </span>
          </div>

          {/* Title */}
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
            {article.title}
          </h1>

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>

          {/* Content */}
          <div
            className="prose prose-base max-w-none text-foreground-800 leading-relaxed
              [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-10 [&_h2]:mb-4
              [&_p]:text-base [&_p]:leading-relaxed [&_p]:mb-5 [&_p]:text-foreground-700
              [&_strong]:text-foreground-950"
            dangerouslySetInnerHTML={{ __html: article.content }}
          ></div>

          {/* Divider */}
          <div className="border-t border-background-200/60 my-10"></div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section>
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/articles/${ra.slug}`}
                    className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/80 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-500 mt-2 block">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}